import { useConfigurator } from '@/composables/configurator/useConfigurator'
const triedSave = ref(false)

export const useValidations = () => {
  const { t } = useI18n({ useScope: 'global' })

  const errors = computed(() => {
    const e: Record<string, Record<string, string>> = {}
    const { payload } = useConfigurator()

    const general = payload.value.general_information
    const generalSection: Record<string, string> = {}
    if (!general.agent_name.trim()) {
      generalSection.agent_name = t('agent_name_required')
    }
    if (!Array.isArray(general.supported_languages) || general.supported_languages.length === 0) {
      generalSection.supported_languages = t('language_required')
    }

    if (Object.keys(generalSection).length) {
      e.general_information = generalSection
    }

    const inactivity = payload.value.inactivity
    const conversationalFlowSection: Record<string, string> = {}
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (inactivity) {
      if (
        !inactivity.user_away_timeout ||
        inactivity.user_away_timeout < 5 ||
        inactivity.user_away_timeout > 15
      ) {
        conversationalFlowSection.user_away_timeout = t('user_away_timeout_required')
      }
      if (
        !inactivity.max_retries_user_away ||
        inactivity.max_retries_user_away < 1 ||
        inactivity.max_retries_user_away > 10
      ) {
        conversationalFlowSection.max_retries_user_away = t('max_retries_user_away_required')
      }
      if (!inactivity.inactivity_output.trim()) {
        conversationalFlowSection.inactivity_output = t('inactivity_output_required')
      }
    }

    if (Object.keys(conversationalFlowSection).length) {
      e.conversational_flow = conversationalFlowSection
    }

    if (payload.value.customer_intents.length === 0) {
      e.customer_intents = {
        _: t('at_least_one_intent'),
      }
    }

    const callDurationLimits = payload.value.call_duration_limits
    const callDurationLimitsSection: Record<string, string> = {}
    if (!callDurationLimits.max_call_duration_output.trim()) {
      callDurationLimitsSection.max_call_duration_output = t('max_call_duration_output_required')
    }

    if (Object.keys(callDurationLimitsSection).length) {
      e.call_duration_limits = callDurationLimitsSection
    }

    const formSchema = payload.value.experimental?.form_schema ?? []
    const experimentalSection: Record<string, string> = {}
    formSchema.forEach((field, index) => {
      const hasName = !!field.name.trim()
      const hasDescription = !!field.description.trim()
      if (hasName && !hasDescription) {
        experimentalSection[`form_schema.${String(index)}.description`] = t(
          'experimental.form_schema.description_required',
        )
      }
      if (hasDescription && !hasName) {
        experimentalSection[`form_schema.${String(index)}.name`] = t(
          'experimental.form_schema.name_required',
        )
      }
    })

    if (Object.keys(experimentalSection).length) {
      e.experimental = experimentalSection
    }

    return e
  })

  const isValid = computed(() => Object.keys(errors.value).length === 0)

  const visibleErrors = computed(() => (triedSave.value ? errors.value : {}))

  const visibleErrorList = computed(() => (triedSave.value ? Object.values(errors.value) : []))

  const markTriedSave = () => (triedSave.value = true)
  const resetErrors = () => (triedSave.value = false)

  const scrollToFirstError = async () => {
    await nextTick()

    const fieldKeys = Object.values(errors.value).flatMap((sectionErrors) =>
      Object.keys(sectionErrors),
    )
    const el = fieldKeys
      .map((fieldKey) => document.querySelector(`[data-error-for="${fieldKey}"]`))
      .find((element): element is HTMLElement => element !== null)

    el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    el?.focus()
  }

  return {
    isValid,
    errors,
    visibleErrors,
    visibleErrorList,
    markTriedSave,
    triedSave,
    resetErrors,
    scrollToFirstError,
  }
}
